import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Plus, Download, Ban, Receipt } from 'lucide-react';
import { Header, PageContainer } from '@/components/layout';
import {
  Button,
  Card,
  CardBody,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  EmptyState,
  Select,
  ConfirmDialog,
  InvoiceStatusBadge,
} from '@/components/ui';
import { useInvoices, useVoidInvoice } from '@/hooks/useInvoices';
import { useClients } from '@/hooks/useClients';
import { useAuthStore } from '@/stores/auth';
import { formatCurrency, formatDate, InvoiceStatus } from '@aelita/shared';
import type { Invoice } from '@aelita/shared';

export const Route = createFileRoute('/invoices/')({
  component: InvoicesPage,
});

function InvoicesPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin';

  const [filters, setFilters] = useState<{
    status?: string;
    clientId?: string;
    page?: number;
  }>({ page: 1 });
  const [voidingInvoice, setVoidingInvoice] = useState<Invoice | null>(null);

  const { data, isLoading, isError } = useInvoices(filters);
  const { data: clientsData } = useClients({});
  const voidInvoice = useVoidInvoice();

  const invoices = data?.data ?? [];
  const pagination = data?.pagination;
  const clients = clientsData?.data ?? [];

  const handleVoid = async () => {
    if (!voidingInvoice) return;
    try {
      await voidInvoice.mutateAsync(voidingInvoice.id);
      setVoidingInvoice(null);
    } catch {
      // error surfaced via mutation state
    }
  };

  function handleExportCsv() {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.clientId) params.set('clientId', filters.clientId);
    window.open(`/api/invoices/export.csv?${params.toString()}`, '_blank');
  }

  return (
    <>
      <Header
        title="Invoices"
        subtitle="Manage billing and payment status"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              leftIcon={<Download className="h-4 w-4" />}
              onClick={handleExportCsv}
            >
              Export CSV
            </Button>
            {isAdmin && (
              <Button
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => navigate({ to: '/invoices/new' })}
              >
                New Invoice
              </Button>
            )}
          </div>
        }
      />

      <PageContainer>
        {/* Filters */}
        <Card className="mb-6">
          <CardBody className="flex items-center gap-4 flex-wrap">
            <Select
              options={[
                { value: '', label: 'All Statuses' },
                { value: InvoiceStatus.DRAFT, label: 'Draft' },
                { value: InvoiceStatus.SENT, label: 'Sent' },
                { value: InvoiceStatus.PAID, label: 'Paid' },
                { value: InvoiceStatus.EXPIRED, label: 'Expired' },
                { value: InvoiceStatus.VOID, label: 'Void' },
              ]}
              value={filters.status ?? ''}
              onChange={(e) =>
                setFilters((f) => ({ ...f, status: e.target.value || undefined, page: 1 }))
              }
              className="w-40"
            />
            <Select
              options={[
                { value: '', label: 'All Clients' },
                ...clients.map((c: any) => ({ value: c.id, label: c.name })),
              ]}
              value={filters.clientId ?? ''}
              onChange={(e) =>
                setFilters((f) => ({ ...f, clientId: e.target.value || undefined, page: 1 }))
              }
              className="w-52"
            />
          </CardBody>
        </Card>

        {/* Table */}
        <Card>
          {isLoading ? (
            <CardBody>
              <div
                className="space-y-3 animate-pulse"
                aria-busy="true"
                aria-label="Loading invoices"
              >
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-10 bg-gray-100 dark:bg-gray-800 rounded" />
                ))}
              </div>
            </CardBody>
          ) : isError ? (
            <CardBody>
              <p className="text-red-600 dark:text-red-400">Failed to load invoices. Please try again.</p>
            </CardBody>
          ) : invoices.length === 0 ? (
            <EmptyState
              icon={Receipt}
              title="No invoices yet"
              description="Generate your first invoice from unpaid time entries"
              action={
                isAdmin
                  ? { label: 'New Invoice', onClick: () => navigate({ to: '/invoices/new' }) }
                  : undefined
              }
            />
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>Invoice #</TableHeader>
                  <TableHeader>Client</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Total</TableHeader>
                  <TableHeader>Issued</TableHeader>
                  <TableHeader>Due</TableHeader>
                  <TableHeader>Paid</TableHeader>
                  <TableHeader className="w-24">{''}</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((invoice: Invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>
                      <Link
                        to="/invoices/$invoiceId"
                        params={{ invoiceId: invoice.id }}
                        className="font-mono font-medium text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 hover:underline"
                      >
                        {invoice.invoiceNumber}
                      </Link>
                    </TableCell>
                    <TableCell>{(invoice as any).client?.name ?? '—'}</TableCell>
                    <TableCell>
                      <InvoiceStatusBadge status={invoice.status} />
                    </TableCell>
                    <TableCell className="font-medium">{formatCurrency(invoice.total)}</TableCell>
                    <TableCell>
                      {invoice.issuedAt ? formatDate(new Date(invoice.issuedAt)) : '—'}
                    </TableCell>
                    <TableCell>
                      {invoice.dueAt ? formatDate(new Date(invoice.dueAt)) : '—'}
                    </TableCell>
                    <TableCell>
                      {invoice.paidAt ? formatDate(new Date(invoice.paidAt)) : '—'}
                    </TableCell>
                    <TableCell>
                      {isAdmin &&
                        invoice.status !== InvoiceStatus.VOID &&
                        invoice.status !== InvoiceStatus.PAID && (
                          <button
                            className="p-1 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-950"
                            title="Void invoice"
                            onClick={() => setVoidingInvoice(invoice)}
                          >
                            <Ban className="h-4 w-4" />
                          </button>
                        )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} invoices)
            </p>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={!pagination.hasPrev}
                onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) - 1 }))}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!pagination.hasNext}
                onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) + 1 }))}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </PageContainer>

      <ConfirmDialog
        isOpen={!!voidingInvoice}
        onClose={() => setVoidingInvoice(null)}
        onConfirm={handleVoid}
        title="Void Invoice"
        message={`Are you sure you want to void invoice ${voidingInvoice?.invoiceNumber}? This cannot be undone.`}
        confirmLabel="Void Invoice"
        isLoading={voidInvoice.isPending}
      />
    </>
  );
}
